import {Pipe, PipeTransform} from '@angular/core';
import {LanguagePipe} from './language.pipe';

/**
 * Renders a number using the active locale's digit glyphs.
 *
 * Two sources, in priority order:
 *
 * 1. The `0123456789` translation row, read out of the runtime
 *    `LanguagePipe` map (populated by `AppComponent.ngOnInit` from the
 *    hidden `languageStringText` spans, which Angular has already
 *    localized from `messages.<loc>.xtb`). This is the authoritative
 *    table and it covers **every** locale that ships one — currently
 *    `am`, `ar`, `bn`, `hi`, `ta`. Adding a sixth is a bundle edit,
 *    not a code change.
 * 2. The hardcoded `ar` / `fa` tables below, kept as a fallback for
 *    the window before the map is populated and for `fa`, which has
 *    no bundle in this repo.
 *
 * Anything else falls through to the value unchanged, which is the
 * correct answer for the 16 locales that use Western digits.
 *
 * NOTE this is a *display* transform only. Never feed its output back
 * into arithmetic, into a form control, or into a filter comparison —
 * use `LanguagePipe.delocalizeNumber` to go the other way.
 */
@Pipe({
  name: 'numberFormat'
})
export class NumberFormattingPipe implements PipeTransform {
  static defaultLanguage = 'en';

  static setDefaultLanguage(defaultLanguage: string) {
    NumberFormattingPipe.defaultLanguage = defaultLanguage;
  }

  transform(value: number | string): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'number') {
      value = value.toString();
    }

    // Preferred path: whatever the active bundle declares.
    const table = LanguagePipe.digitTable();
    if (table) {
      return value.replace(/[0-9]/g, (digit) => table[+digit] || digit);
    }

    switch (NumberFormattingPipe.defaultLanguage) {
      case 'ar':
        const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
        return value.replace(/\d/g, (digit) => arabicNumerals[+digit]);
      case 'fa': // Persian (Farsi)
        const farsiNumerals = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
        return value.replace(/\d/g, (digit) => farsiNumerals[+digit]);
      default: // Fallback to original value (e.g., for English)
        return value;
    }
  }
}
